import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Search, Mail } from "lucide-react";

/** Users list + admin-message dispatcher. */
export default function CmsUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/users", { params: { limit: 100, search: q || undefined } });
      setUsers(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resendMagic = async (email) => {
    if (!email) return;
    try {
      await http.post("/admin/cms/users/resend-magic-link", { email });
      toast.success(`Magic link queued for ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to queue magic link");
    }
  };

  const sendMessage = async () => {
    if (!target || !body.trim()) return;
    setSending(true);
    try {
      await http.post(`/admin/users/${target.id}/message`, {
        subject: subject || undefined,
        message: body.trim(),
      });
      toast.success(`Message sent to ${target.email || target.name}`);
      setTarget(null);
      setBody("");
      setSubject("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl" data-testid="cms-users">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
      <p className="mt-1 text-sm text-slate-500">Search, message and resend magic links — {total} registered.</p>

      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cms-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, email, phone…"
            className="pl-8"
          />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Searching…" : "Search"}</Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id || u.email} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{u.name || "—"}</div>
                    <div className="text-xs text-slate-500">{u.email || u.phone || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{u.platform_role || (u.is_admin ? "admin" : "user")}</td>
                  <td className="px-3 py-2 text-slate-700">{u.status || "active"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setTarget(u)}
                      data-testid={`cms-users-message-${u.id || u.email}`}
                      className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
                    >
                      <Mail className="h-3 w-3" /> Message
                    </button>
                    {u.email && (
                      <button
                        onClick={() => resendMagic(u.email)}
                        data-testid={`cms-users-magic-${u.email}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-sky-300 hover:bg-sky-50"
                      >
                        Send magic link
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-500">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {target && (
          <aside className="sticky top-4 h-fit rounded-xl border border-emerald-200 bg-white p-4 shadow-sm" data-testid="cms-users-message-panel">
            <div className="mb-1 text-xs uppercase tracking-wider text-emerald-700">Compose</div>
            <div className="text-sm font-medium text-slate-900">To: {target.name || target.email}</div>
            <Input
              className="mt-3"
              placeholder="Subject (optional)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="cms-users-message-subject"
            />
            <Textarea
              rows={6}
              className="mt-2"
              placeholder="Write your message… delivered as a dashboard notification and via email."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="cms-users-message-body"
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
              <Button
                onClick={sendMessage}
                disabled={sending || !body.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="cms-users-message-send"
              >
                <Send className="mr-1 h-4 w-4" /> {sending ? "Sending…" : "Send"}
              </Button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
